import { useMemo } from "react";

import { useModules } from "src/api/modulesApi";
import { useWorkspace } from "src/api/queries";
import { ModuleKind } from "src/clients/flow-api";
import { DashboardPageParams } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

export type WorkspaceFeatureGates = {
  entitiesEnabled: boolean;
  featuresEventsEnabled: boolean;
  // [DEPRECATED] This gate is no longer used.
  // Will be removed in RES-5317.
  cmCopilotEnabled: boolean;
  agentsEnabled: boolean;
  // BYOK (bring-your-own-key) LLM credentials. Replaces the legacy
  // `byok-llm-credentials` URL feature flag.
  byokEnabled: boolean;
  // True while the underlying workspace/modules queries are still resolving.
  // Until they settle, the gate booleans default to `false`, which is
  // indistinguishable from a genuinely disabled feature. Callers that mount
  // routes off these gates should use this to avoid flashing a 404 while the
  // gate is still being determined.
  isLoading: boolean;
};

const DefaultFeatureGates: Omit<WorkspaceFeatureGates, "isLoading"> = {
  entitiesEnabled: false,
  featuresEventsEnabled: false,
  cmCopilotEnabled: false,
  agentsEnabled: false,
  byokEnabled: false,
};

// Agent Builder enablement lives on the agents module config
// `feature_flag_tktl_agents` boolean, not on a workspace feature gate. A
// deployed agents module reads as enabled unless the flag is explicitly
// `false`, matching `useIsAgentsBuilderFeatureEnabled`.
export const useWorkspaceFeatureGates = (): WorkspaceFeatureGates => {
  const { wsId } = useParamsDecode<DashboardPageParams>();
  const { data: workspace, isLoading: isWorkspaceLoading } = useWorkspace(wsId);
  const { data: agentsModules, isLoading: isModulesLoading } = useModules({
    workspaceId: workspace?.id,
    kind: ModuleKind.AGENTS,
  });

  // The modules query is disabled until the workspace id resolves, so while
  // the workspace is still loading the modules query reports `isLoading:
  // false` despite not having run yet. Treat that as still-loading too.
  const isLoading = isWorkspaceLoading || !workspace || isModulesLoading;

  return useMemo(() => {
    const agentsModule = workspace
      ? agentsModules?.find(
          (m) => m.workspaceId === workspace.id && m.spec !== null,
        )
      : undefined;
    const agentsFlag = agentsModule?.desiredSpec.config
      .feature_flag_tktl_agents as boolean | undefined;
    const agentsEnabled = agentsModule !== undefined && (agentsFlag ?? true);

    if (!workspace || !workspace.feature_gates) {
      return { ...DefaultFeatureGates, agentsEnabled, isLoading };
    }

    const { feature_gates } = workspace;

    return {
      entitiesEnabled: feature_gates.entity ?? false,
      featuresEventsEnabled: feature_gates.feature ?? false,
      cmCopilotEnabled: feature_gates.cm_copilot ?? false,
      byokEnabled: feature_gates.byok ?? false,
      agentsEnabled,
      isLoading,
    };
  }, [workspace, agentsModules, isLoading]);
};
